/* eslint-disable max-lines */
import {
  ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, 
  ViewChild, Renderer2, AfterViewInit, ChangeDetectorRef
} from '@angular/core';
import { Subscription, combineLatest, zip, of, Observable, iif, EMPTY } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { delay, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { parse } from 'date-fns';
import isEqual from 'lodash-es/isEqual';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { hotelDetailRootRoute } from '@hotels/hotel-detail/hotel-detail-routing.module';
import { HotelFilterProvider, NonRefundableTypes } from '@shared/hotel-itinerary/hotel-filters';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { 
  dateHotelValidation, 
  formatDate, 
  mapPayloadHotel, 
  mapPayloadHotelByHours 
} from '@hotels/hotel-results/mappers/hotel-results.mapper';
import { AppFacade } from '@app/app.facade';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { HotelResultsAbstractComponent } from '@hotels/hotel-results/abstract/hotel-results.abstract';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { HOTEL_BYHOURS_RESULTS_ROUTE, HOTELS_FILTERS_ROUTE } from '@hotels/hotel-results/constants/routes';
import { formatDateByRegex, blockSearchByDates, isNationalSearch, canSearch } from '@ux/mappers/date.mapper';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { environment } from '@environment';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { ToastrTypes } from '@app/entities/toastr';
import { hotelsHomeRootRoute } from '@hotels/hotels-home/hotels-home-routing.module';
import { RappiTravelModules } from '@ux/entities/modules';
import { TypeGeneralWidget } from '@shared/widgets/components/general-widget/general-widget.enum';
import { HotelFilterSearchPayload } from '@hotels/hotel-results/entities/hotel-filter-search.payload';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { HOTELS_STEP_WIDGET } from '@shared/widgets/constants/positions.constants';
import { quaryFilterParams } from '@hotels/hotel-results/mappers/hotel-filters.mapper';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { HotelDestinationInputType, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { MapMarkerInputs } from '@shared/map/constants/map.constants';
import { BRANCH_CODE, SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SessionStorageService } from '@app/services/session-storage.service';
import { isAnInternationalTripBySearch } from '@app/mappers/is-international-trip.mapper';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { HotelSearchPayload } from '@hotels/hotel-results/entities/hotel-search.payload';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { FinancialPartnersInfoVM } from '@shared/benefits/entities/benefits';
import { productHomeRoute } from '@app/constants/app-routing.constants';
import { NavigationService } from '@app/services/navigation.service';

@Component({
  selector: 'app-hotel-results',
  templateUrl: './hotel-results.container.html',
  styleUrls: ['./hotel-results.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelResultsContainer extends HotelResultsAbstractComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('list', { static: true }) list: ElementRef;

  private subscriptions: Subscription[] = [];
  private scrollHotelMarginTop: number = 66;
  private hotelsCalendarRoute: string = 'hotels-calendar';
  private activateTop: boolean = false;
  private resultCount: number = 0;
  private savedScrollPosition = 0;
  private isFreezeComponent: boolean = false;
  private unlistener: () => void;

  private editListener: Subscription;
  private getDetachedChanges: Subscription;

  public swipeStyle: SwipeType = SwipeType.FULL;
  public stickyFilterBar: boolean;
  public showFinancialPartners: boolean = false;
  public financialPartnersInfo: FinancialPartnersInfoVM;

  constructor(
    private readonly translateService: TranslocoService,
    private readonly renderer: Renderer2,
    private readonly hotelDetailFacade: HotelDetailFacade,
    private readonly headerService: HeaderService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly cdr: ChangeDetectorRef,
    protected facade: HotelResultsFacade,
    protected baseSearchBarFacade: BaseSearchBarFacade,
    protected appFacade: AppFacade,
    protected route: ActivatedRoute,
    protected router: Router,
    protected viewportScroller: ViewportScroller,
    protected mobileDeviceService: MobileDeviceService,
    protected amplitude: PropertiesAmplitudeService,
    protected domManipulateService: DomManipulateService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected placeNamePipe: PlaceNamePipe,
    protected avoService: AvoService,
    protected navigationService: NavigationService,
    @Inject(IS_BROWSER) public isBrowser: boolean) {
    super(
      facade, appFacade, route, router, viewportScroller, mobileDeviceService,
      amplitude, domManipulateService, triggerAmplitudeService, placeNamePipe, avoService, baseSearchBarFacade,
      navigationService,
    );
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.facade.clearHotelResults();
      this.widgets = [...this.baseWidgets];
      this.subscriptions.push(
        this.params$.pipe(
          map(params => params?.destination_type as HotelDestinationInputType),
          distinctUntilChanged(),
          tap((destinationType) => {
            switch (destinationType) {
              case HotelDestinationInputType.SearchBar:
                this.facade.searchLocationHotel({
                  CheckinDate: this.route?.snapshot?.params?.['check_in'],
                  CheckoutDate: this.route?.snapshot?.params?.['check_out'],
                  CurrencyCode: this.route?.snapshot?.params?.['currency_code'],
                  DestinationCode: this.route?.snapshot?.params?.['destination_id'],
                  Rooms: this.route?.snapshot?.params?.['rooms'],
                  PackHour:this.route?.snapshot?.params?.['pack_hour']
                }, this.isByHours);
                break;
              default:
                this.editListener?.unsubscribe();
                this.editListener = null;
                this.getDetachedChanges?.unsubscribe();
                this.getDetachedChanges = null;
                this.launchFirstPage();
                this.addEditSearchListener();
                this.addDetachedRouteHandlesListener();
                break;
            }
          })
        ).subscribe()
      );
      this.registerAmplitudeEvent();
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.unlistener = this.renderListener();
    }
  }

  ngOnDestroy(): void {
    this.facade.clearHotelResults();
    this.facade.disabledResultsScroll(true);
    removeSubscriptions(this.subscriptions);
    removeSubscriptions(this.componentSubscriptions);
    if (this.isBrowser) {
      this.unlistener();
    }
    this.editListener?.unsubscribe();
    this.getDetachedChanges?.unsubscribe();
  }

  onAttach(): void {
    this.cdr.detectChanges();
    this.viewportScroller.scrollToPosition([0, this.savedScrollPosition]);
    if (this.isBrowser) {
      this.unlistener = this.renderListener();
    }
    this.isFreezeComponent = false;
  }

  onDetach(): void {
    this.isFreezeComponent = true;
    this.editListener?.unsubscribe();
    this.cdr.detectChanges();
  }

  private get headerProductType(): HeaderProductType {
    return this.isByHours ? HeaderProductType.ByHours : HeaderProductType.Hotels;
  }

  get editSearchIsActivated$(): Observable<boolean> {
    return this.baseSearchBarFacade.editSearchIsActivated$(this.headerProductType);
  }
  
  get isEditButtonVisible$(): Observable<boolean> {
    return this.baseSearchBarFacade.isHeaderEditButtonVisible$;
  }
  get disabledHotelResultsScroll$(): Observable<boolean> {
    return this.facade.disabledHotelResultsScroll$;
  }
  get exclusiveOffer$(): Observable<boolean> {
    return combineLatest([this.isPrime$, this.hasRappiCard$, this.exclusiveOfferFilter$]).
      pipe(map(([isPrime, hasRappiCard, exclusiveOfferFilter]) =>
        ( ((isPrime || hasRappiCard) && !exclusiveOfferFilter) || (!isPrime && !hasRappiCard))));
  }
  get isInternational$(): Observable<boolean> {
    return this.baseSearchBarFacade.searchData$.pipe(map(searchData => !!searchData &&
      isAnInternationalTripBySearch(SearchBy.HOTELS, searchData)));
  }
  get isTopClass$(): Observable<boolean> {
    return combineLatest([this.loadingResults$, this.isMobile$]).pipe(map(([loadingResults, isMobile]) =>
      isMobile && !this.showResultsMap && !loadingResults));
  }
  get checkRefundable$(): Observable<boolean> {
    return this.pickedCheckboxes$.pipe(map(x => x.includes(NonRefundableTypes.Refundable)));
  }
  get enabledBannerPrime$(): Observable<boolean> {
    return combineLatest(
      [this.isMobile$, this.isPrime$]).pipe(
      map(([isMobile, isPrime]) => isMobile && !isPrime));
  }
  get destinationHotelPlace$(): Observable<InputDestinationPlace> {
    return this.baseSearchBarFacade.hotelDestinationPlace$;
  }
  get source(): string {
    return this.route?.snapshot?.queryParams?.[SOURCE_PARAM];
  }

  get branchCode(): string {
    return this.route?.snapshot?.queryParams?.[BRANCH_CODE];
  }

  get hotelName(): string {
    return this.inputDestinationPlace?.hotel_name;
  }

  get isNotSearchedHotel(): boolean {
    return !isNullOrUndefined(this.inputDestinationPlace?.hotel_name) && !this.isByHours;
  }

  get searchBy(): SearchBy {
    return this.isByHours ? SearchBy.BYHOURS : SearchBy.HOTELS;
  }

  public resultsLabel(hotelSearchResultTotalCount: number): string {
    const message = hotelSearchResultTotalCount > 1 ?
      this.translateService.translate('HOTELS_MODULE.RESULT_HOTELS.NUMBER_OF_RESULTS_HOTELS') :
      this.translateService.translate('HOTELS_MODULE.RESULT_HOTELS.SINGULAR_RESULT_HOTELS');
    return `${hotelSearchResultTotalCount} ${message}`;
  }

  public trackByIdx(index: number, item: HotelSearched): number {
    return item.HotelId;
  }

  public navigateToHotelDetail(detail: {hotel: HotelSearched, isOffers?: boolean}): void {
    const { hotel, isOffers } = detail;
    this.hotelDetailFacade.resetTooltips();
    this.facade.hotelSearchData$.pipe(take(1)).pipe(tap(() => this.hotelDetailFacade.fetchHotelDetailSearched(hotel)),
      switchMap(data => zip(of(data),
        this.hotelDetailFacade.hotelDetailSearched$.pipe(filter(hotelDetailSearched => !!hotelDetailSearched)))),
      map(([data]) => ({ data })), 
      tap(({ data }) => {
        const urlSegments = [hotelDetailRootRoute, data.session, data.SearchId, hotel.OptionId, this.rooms];
        const queryParams = isOffers ? { source: EVENT_SOURCE.OFFERS } : { source: EVENT_SOURCE.RESULTS }; 
        this.router.navigate(urlSegments, { queryParams });
      })).subscribe().unsubscribe();
  }

  public activeFilter(): void {
    this.filtersProvider = this.showResultsMap ? HotelFilterProvider.HotelMap : HotelFilterProvider.HotelResults;
    this.router.navigate([{ outlets: { 'outlet': [HOTELS_FILTERS_ROUTE] } }],
      { relativeTo: this.route, skipLocationChange: true, queryParams: { TYPE: this.filtersProvider, source: this.source } });
    this.showMapResultFilters = this.showResultsMap;
    this.showResultsMap = false;
  }

  public selectWidgets(index: number): string[] {
    if (index !== 0 && index % HOTELS_STEP_WIDGET === 0) {
      return this.widgets;
    }
  }

  public toggleSearchPanel(): void {
    this.editSearchIsActivated$.pipe(
      take(1),
      tap(editSearchIsActivated => {
        const current = editSearchIsActivated ? this.headerProductType : null;
        this.baseSearchBarFacade.setEditSearchByProduct(current, this.headerProductType);
        if (!editSearchIsActivated) {
          const departure = parse(this.params.CheckIn, 'yyyy-MM-dd', new Date());
          if (this.isByHours) {
            this.baseSearchBarFacade.selectCalendarDates([departure]);
          } else {
            const arrival = parse(this.params.CheckOut, 'yyyy-MM-dd', new Date());
            this.baseSearchBarFacade.selectCalendarDates([departure, arrival]);
          }
          this.domManipulateService.setBodyScroll(false);
        } else {
          this.domManipulateService.setBodyScroll(true);
        }
      })
    ).subscribe().unsubscribe();
  }

  public redirectToSearch(): void {
    this.toggleSearchPanel();
    setTimeout(() => {
      this.registerViewTypeEvent();
    }, 0);
  }

  public showSelectedFilters(hasFilters: boolean, searchPayload: HotelFilterSearchPayload): boolean {
    if (hasFilters) {
      if (this.searchPayload.Stars.length !== searchPayload.Categories.length ||
        (this.searchPayload.PriceFrom !== searchPayload.PriceFrom && this.searchPayload.PriceTo !== searchPayload.PriceTo)) {
        this.widgets = this.checkWidgetsAvailable(searchPayload);
      }
    } else if (this.widgets.length < this.baseWidgets.length) {
      this.widgets = this.baseWidgets;
    }
    return hasFilters;
  }

  public checkWidgetsAvailable(searchPayload: HotelFilterSearchPayload): string[] {
    let availableWidgets = [...this.baseWidgets];
    const { Categories, PriceFrom, PriceTo } = searchPayload;
    this.searchPayload = { ...this.searchPayload, Stars: Categories };
    if (Categories.length) {
      availableWidgets = availableWidgets.filter(widget => widget !== TypeGeneralWidget.STARS_WIDGET);
    } this.searchPayload = { ...this.searchPayload, PriceFrom: PriceFrom, PriceTo: PriceTo };
    if (!isNullOrUndefined(PriceFrom) && !isNullOrUndefined(PriceTo)) {
      availableWidgets = availableWidgets.filter(widget => widget !== TypeGeneralWidget.PRICE_WIDGET);
    } return availableWidgets;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.router.navigate([{ outlets: { 'outlet': [this.hotelsCalendarRoute] } }],
      {
        state: {
          activeType: type,
          departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.DEPART',
          returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.RETURN',
          searchType: this.searchType
        }, relativeTo: this.route, skipLocationChange: true
      });
  }

  public topAction(): void {
    this.activateTop = true;
    const [, start] = this.viewportScroller.getScrollPosition();
    const change = 0 - start; const increment = 20; let currentTime = 0;
    const animateScroll = () => {
      const [, scrollY] = this.viewportScroller.getScrollPosition(); currentTime += increment;
      const val = (t, b, c, d) => {
        t /= d / 2; if (t < 1) return c / 2 * t * t + b;
        t--; return -c / 2 * (t * (t - 2) - 1) + b;
      }; this.viewportScroller.scrollToPosition([0, val(currentTime, start, change, 200)]);
      if (scrollY > this.scrollHotelMarginTop) setTimeout(animateScroll, increment);
    }; animateScroll();
  }

  public getCityMarker(data: HotelSearched[]): MapMarkerInputs[] {
    if (isNullOrUndefined(data) || data.length === 0) return null;
    const hotel = data[0];
    return [{
      position: { lat: hotel.Latitude, lng: hotel.Longitude },
      label: hotel.HotelName
    }];
  }

  public back(): void {
    this.headerService.goBack();
  }

  public resetTooltips(payload: boolean): void {
    this.hotelDetailFacade.resetTooltips(payload);
  }

  public showRappiCardBanner(index: number, isMobile: boolean): boolean {
    return isMobile ? index === 1 : index === 0;
  }

  public toggleFinancialPartners(data: FinancialPartnersInfoVM = null): void {
    this.financialPartnersInfo = data;
    this.showFinancialPartners = !this.showFinancialPartners;
  }

  private mapPayloadParams(params: Params, isMobile: boolean): HotelSearchPayload {
    return this.isByHours ? 
      mapPayloadHotelByHours(params, isMobile):
      mapPayloadHotel(params, isMobile, this.route.snapshot.queryParams.from);
  }

  private payloadSearchData(searchData: SearchData, params: Params, queryParams: Params): void {
    const payload = { ...searchData };
    const queryZones = quaryFilterParams(queryParams, 'zones');
    const queryCategories = quaryFilterParams(queryParams, 'categories');
  
    if (this.isByHours) {
      this.baseSearchBarFacade.selectCalendarDates([formatDateByRegex(params?.['check_in'])]);
      this.preparePayloadAndSearchHotels(payload, this.isByHours, queryZones, queryCategories);
    } else {
      const dateValidation = 
      dateHotelValidation(formatDateByRegex(params?.['check_in']), formatDateByRegex(params?.['check_out']));
      if (dateValidation && dateValidation.valid) {
        this.baseSearchBarFacade.selectCalendarDates([new Date(dateValidation.departure), new Date(dateValidation.arrival)]);
        this.preparePayloadAndSearchHotels(payload, this.isByHours, queryZones, queryCategories);
      } else {
        const url = 
              `${this.hotelResultsRootRoute}/${payload.hotelDestinationPlace.InputType}/${payload.hotelDestinationPlace.Id}` +
              `/${formatDate(dateValidation.departure)}/${formatDate(dateValidation.arrival)}` +
              `/${environment.countryInfo.currency_code}/${params?.['rooms']}`;
        this.router.navigateByUrl(url);
      }
    }
  }

  private preparePayloadAndSearchHotels(
    payload: SearchData, 
    isByHours: boolean, 
    queryZones: string[], 
    queryCategories: string[]
  ) {
    this.widgets = [...this.baseWidgets];

    if (!isNullOrUndefined(payload.hotelDestinationPlace.Id) && !isNullOrUndefined(payload.hotelDestinationPlace.NearestIata)) {
      const searchBy = this.isByHours ? SearchBy.BYHOURS : SearchBy.HOTELS;
      this.baseSearchBarFacade.saveSearchData(payload, searchBy);
    }
  
    const searchParams = { ...this.params };
    const filterParams = { Zones: queryZones, Categories: queryCategories };
  
    this.facade.searchHotels(searchParams, filterParams, isByHours, payload.hourType);
  }
  

  private registerAmplitudeEvent(): void {
    const searchBy = this.isByHours ? SearchBy.BYHOURS : SearchBy.HOTELS;
    this.subscriptions.push(
      combineLatest([
        this.amplitude.searchProperties$(searchBy),
        this.facade.hotelFiltersPayload$.pipe(filter(x => !isNullOrUndefined(x.ResultsCount))),
        this.facade.isLoadingHotelResults$,
      ]).pipe(
        switchMap(([searchProps, payload, isLoading]) => this.route.queryParamMap.pipe(
          take(1),
          map(queries => ({
            eventName: AVO_EVENTS.VIEW_TRAVEL_RESULTS_SCREEN,
            eventParams: { SOURCE: queries.get(SOURCE_PARAM), RESULTS: payload.ResultsCount },
            searchProps, isLoading, payload
          })),
          switchMap(({ eventName, eventParams }) =>
            this.avoService.getEventTriggerProps$(eventName, eventParams, searchProps).pipe(
              filter(() => !isLoading && this.resultCount !== payload.ResultsCount),
              tap(trigger => {
                this.AvoDispatch.viewTravelResultsScreen({
                  ...this.avoService.avoBaseProperties(trigger),
                  ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
                  source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
                  resultsCount: trigger?.EVENT_PROPS?.RESULTS as number,
                  searchFlowType: undefined,
                  travelSegment: undefined,
                });
                this.resultCount = payload.ResultsCount;
              })
            ))
        ))
      ).subscribe()
    );
  }

  private setGuestsByUrlParams(): void {
    this.params.Rooms.forEach((room, index) => {
      if (index > 0) {
        this.baseSearchBarFacade.addNewRoom({
          id: index, [TravelerTypes.ADULTS]: 2, [TravelerTypes.CHILDREN]: 0, childrenAges: []
        });
      }
      this.setGuestsPerRoom({
        id: index, [TravelerTypes.ADULTS]: room.Adults,
        [TravelerTypes.CHILDREN]: room.Children, childrenAges: room.ChildrenAges
      });
    });
  }

  private setGuestsPerRoom(event: Room): void {
    this.baseSearchBarFacade.setGuestsPerRoom(event);
  }

  private goBack(): void {
    this.isMobile$.subscribe(isMobile => {
      this.appFacade.historyPop();
      if (!isMobile) this.router.navigate([['/']]); else this.router.navigate([hotelsHomeRootRoute, productHomeRoute]);
    }).unsubscribe();
  }

  private renderListener(): () => void {
    return this.renderer.listen(window, 'scroll', () => {
      const [scrollX, scrollY] = this.viewportScroller.getScrollPosition();
      if (!this.isFreezeComponent) {
        this.savedScrollPosition = scrollY;
      }
      if (scrollY === this.scrollHotelMarginTop && this.activateTop) {
        this.activateTop = false; 
        this.viewportScroller.scrollToPosition([0, 0]);
      }
      this.stickyFilterBar = scrollY >= 43;
      this.cdr.detectChanges();
    });
  }

  private launchFirstPage(): void {
    const params = this.navigationService.params;
    const queryParams = this.navigationService.queryParams;
    let getDestinationListener = this.isMobile$.pipe(
      take(1),
      tap((isMobile) => {
        this.setParams(isMobile, params, queryParams);
      }),
      switchMap(() => this.searchByParams(params, queryParams).pipe(
        delay(0),
        tap(() => {
          getDestinationListener?.unsubscribe();
          getDestinationListener = null;
        })
      ))
    ).subscribe();
    
  }

  private setParams(isMobile: boolean, params: Params, queryParams: Params): void {
    this.params = this.mapPayloadParams(params, isMobile);
    const branchCode = queryParams?.['branch_code'];
    const salesChannel = queryParams?.['sales_channel'];
    if (branchCode) {
      this.baseSearchBarFacade.setBranchCode(branchCode);
    }
    if (salesChannel) {
      this.baseSearchBarFacade.setSalesChannel(salesChannel);
    }
    if (!this.isByHours) {
      this.setGuestsByUrlParams();
    }
    this.baseSearchBarFacade.searchDestinationPlaceInfo({
      id: params?.['destination_id'],
      type: params?.['destination_type'] as HotelDestinationInputType
    });
  }

  private addDetachedRouteHandlesListener(): void {
    this.getDetachedChanges = this.navigationService.getDetachedChangesByComponent$
      .pipe(
        tap(({ changes, component }) => {
          if (this.editListener?.closed && !changes?.has(component) && !this.isFreezeComponent) {
            this.addEditSearchListener();
          }
        })
      ).subscribe()
  }

  private addEditSearchListener(): void {
    const scope = this.isByHours ? HOTEL_BYHOURS_RESULTS_ROUTE : this.hotelResultsRootRoute;
    this.editListener = this.navigationService.detectPathChange$(scope).pipe(
      filter(({ params }) => !this.isFreezeComponent && params?.['destination_type'] !== HotelDestinationInputType.SearchBar),
      switchMap(({ params, queryParams }) => this.isMobile$.pipe(
        distinctUntilChanged(),
        map(isMobile => ({ isMobile, params, queryParams }))
      )),
      tap(({ isMobile, params, queryParams }) => {
        this.setParams(isMobile, params, queryParams);
      }),
      switchMap(({ params, queryParams }) => this.editSearchIsActivated$.pipe(
        take(1),
        switchMap((editSearchIsActivated) => {
          if (!editSearchIsActivated) {
            return this.searchByParams(params, queryParams);
          } else {
            return EMPTY;
          }
        })
      ))
    ).subscribe();
  }

  private searchByParams(params: Params, queryParams: Params): Observable<unknown> {
    return this.baseSearchBarFacade.hotelDestinationPlace$
      .pipe(
        filter(destination => {
          return !!destination.Id && !!destination.InputType && !!destination.NearestIata &&
            params?.['destination_id'] === destination.Id.toString();
        }),
        take(1),
        tap(destination => {
          this.sessionStorageService.setEncodeDestinationType(destination.InputType);
          this.sessionStorageService.setEncodeDestinationId(destination.Id);
          this.sessionStorageService.setEncodeDestinationIATA(destination.NearestIata);
        }),
        switchMap(input => {
          return iif(
            () => input.InputType === HotelDestinationInputType.hotel ? !input.address : !input.NameFull,
            this.placeNamePipe.transform(input.NearestIata, PlaceSelectors.Name), of(null)
          ).pipe(
            map(placeName => {
              if (placeName) {
                switch (input.InputType) {
                  case HotelDestinationInputType.hotel:
                    return { params, destination: { ...input, address: placeName } };
                  default:
                    return { params, destination: { ...input, NameFull: placeName } };
                }
              } else {
                return { params, destination: input };
              }
            }),
            tap(({ destination }) => {
              this.inputDestinationPlace = destination;
            }),
            switchMap(({ destination }) => zip(
              this.placeNamePipe.transform(destination.NearestIata, PlaceSelectors.Country),
              this.placeNamePipe.transform(destination.NearestIata, PlaceSelectors.City)
            ).pipe(
              take(1),
              map(([country, city]) => {
                const InputType = destination.InputType;
                const baseInfo = {
                  Id: destination.Id,
                  InputType,
                  NearestIata: destination?.NearestIata,
                  Country: country
                };
                switch (InputType) {
                  case HotelDestinationInputType.hotel:
                    return {
                      country, city,
                      destinationFormat: {
                        ...baseInfo,
                        hotel_name: destination.hotel_name ? destination.hotel_name : city,
                        address: destination.address ? destination.address : `${city}, ${country}`,
                      }
                    };
                  default:
                    return {
                      country, city,
                      destinationFormat: {
                        ...baseInfo,
                        Name: destination.Name ? destination.Name : city,
                        NameFull: destination.NameFull ? destination.NameFull : `${city}, ${country}`,
                      }
                    };
                }
              }),
              tap(({ destinationFormat }) => {
                this.baseSearchBarFacade.selectHotelDestinationPlace(destinationFormat);
              }),
              switchMap(({ country, city, destinationFormat }) => this.baseSearchBarFacade.searchData$
                .pipe(
                  filter(searchData => isEqual(searchData.hotelDestinationPlace, destinationFormat)),
                  take(1),
                  switchMap((searchData) => this.editSearchIsActivated$.pipe(
                    take(1),
                    map(editSearchIsActivated => ({
                      searchData, country, city, destination: destinationFormat, params, editSearchIsActivated
                    }))
                  ))
                ))
            ))
          );
        }),
        tap(({ searchData, destination, country, city, editSearchIsActivated }) => {
          if (!editSearchIsActivated) {
            const rqs = {
              ...this.params.RequestSettings,
              BranchCode: searchData.branchCode,
              SalesChanelCode: searchData.salesChannel,
            };
            this.params = {
              ...this.params,
              RequestSettings: rqs
            };
            if (canSearch(RappiTravelModules.hotels)) {
              this.payloadSearchData(searchData, params, queryParams);
            } else {
              this.exeSearchByDates(destination, country, city, params, searchData, queryParams);
            }
          }
        }),
        switchMap(() => EMPTY)
      );
  }

  private exeSearchByDates(
    destination: InputDestinationPlace, country: string, city: string,
    params: Params, searchData: SearchData, queryParams: Params
  ): void {
    const destinationPlace = { code: destination.NearestIata, country, description: city } as AutocompletePlace;
    if (!blockSearchByDates(destinationPlace, formatDateByRegex(params?.['check_in']))) {
      this.payloadSearchData(searchData, params, queryParams);
    } else {
      this.goBack();
      const messageType = isNationalSearch(destinationPlace) ? 'HOTEL_NATIONAL' : 'HOTEL_INTERNATIONAL';
      this.appFacade.fetchToastrMessage({
        message: this.translateService.translate(`BLOCK_SEARCH_MESSAGE.${messageType}`), type: ToastrTypes.ERROR
      });
    }
  }
}
